import { useRef, useState } from 'react';

import clsx from 'clsx';

import { extractPdfContent } from '../../utils/pdfjs';

import { Label } from './utils/Label';
import { SubLabel } from './utils/SubLabel';

interface DropzoneFieldProps {
  id: string;
  label?: React.ReactNode;
  accept?: string;
  maxSize?: number;
  disabled?: boolean;
  onChange: (text: string | null, error?: Error) => void;
  errorMessage?: string;
  subLabel?: string;
  showSubLabel?: boolean;
  className?: string;
}

export function DropzoneField({
  id,
  label,
  accept = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  maxSize = 2048000, // 2MB default
  disabled = false,
  onChange,
  errorMessage = '',
  subLabel = '',
  showSubLabel = false,
  className = '',
}: DropzoneFieldProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setIsDragging(true);
    } else if (e.type === 'dragleave') {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files?.[0]) {
      handleFile(files[0]);
    }
  };

  const isValidFileType = (file: File) => {
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    return validTypes.includes(file.type);
  };

  const handleFile = (file: File) => {
    if (!isValidFileType(file)) {
      onChange(
        null,
        new Error('Invalid file type. Please upload a PDF or Word document.'),
      );
      setCurrentFile(null);
      return;
    }

    if (file.size > maxSize) {
      onChange(
        null,
        new Error(`File size must be less than ${maxSize / 1000000}MB`),
      );
      setCurrentFile(null);
      return;
    }

    setCurrentFile(file);
    const fileURL = URL.createObjectURL(file);

    if (file.type === 'application/pdf') {
      extractPdfContent(fileURL)
        .then((extractedText) => {
          onChange(extractedText);
        })
        .catch((error) => {
          onChange(null, error);
          setCurrentFile(null);
          console.error('Error extracting text from PDF:', error);
        });
    } else {
      // For Word documents, you'll need to implement or import a Word document parser
      onChange(null, new Error('Word document processing not yet implemented'));
      setCurrentFile(null);
    }
  };

  const handleClick = () => {
    if (disabled) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) handleFile(file);
    };
    input.click();
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentFile(null);
    onChange(null);
  };

  const dropzoneClassName = clsx(
    'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
    {
      'border-gray-300 hover:border-gray-400': !isDragging && !disabled,
      'border-blue-400 bg-blue-50': isDragging && !disabled,
      'border-gray-200 bg-gray-50 cursor-not-allowed': disabled,
      'border-red-500': errorMessage,
    },
  );

  return (
    <div className={className}>
      {label && <Label id={id}>{label}</Label>}
      {showSubLabel && <SubLabel id={id}>{subLabel}</SubLabel>}
      <div
        ref={dropzoneRef}
        className={dropzoneClassName}
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="space-y-2">
          {currentFile ? (
            <div className="flex items-center justify-center space-x-2">
              <span className="text-gray-600">{currentFile.name}</span>
              <button
                aria-label="Remove file"
                className="rounded-full bg-gray-200 p-1 hover:bg-gray-300"
                onClick={handleRemoveFile}
              >
                <svg
                  className="h-4 w-4 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          ) : (
            <>
              <p className="text-gray-600">
                {disabled
                  ? 'Upload disabled'
                  : 'Drag and drop your file here, or click to select'}
              </p>
              <p className="text-sm text-gray-500">
                Supported files: PDF, Word documents (max {maxSize / 1000000}MB)
              </p>
            </>
          )}
        </div>
      </div>
      {errorMessage && (
        <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
      )}
    </div>
  );
}
